// Scheduled check-in calls: "call me Tuesdays and Thursdays at 6:30". Stored in
// the user's local terms (weekday 0 = Sun … 6 = Sat, HH:MM) and read against
// their tzOffsetMinutes at ring time.

export const MAX_CHECKINS_PER_WEEK = 3
// The cron runs every 5 minutes; a missed tick still rings within this window.
export const CHECKIN_WINDOW_MIN = 20

export type CheckinSchedule = { days: number[]; time: string }

const NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

// "18:30", "6:30pm", "6pm", "06.30" → "18:30"; null if not a time.
export function parseCheckinTime(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const m = raw
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?:[:.h](\d{2}))?\s*(am|pm)?$/)
  if (!m) return null
  let h = Number(m[1])
  const min = Number(m[2] ?? 0)
  if (m[3] === 'pm' && h < 12) h += 12
  if (m[3] === 'am' && h === 12) h = 0
  if (h > 23 || min > 59) return null
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

// Weekday names ("tue", "Thursday") or numbers 0–6 → sorted unique days.
export function parseCheckinDays(raw: unknown): number[] {
  const list = Array.isArray(raw) ? raw : []
  const out = new Set<number>()
  for (const d of list) {
    if (typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 6) out.add(d)
    else if (typeof d === 'string') {
      const i = NAMES.indexOf(d.trim().toLowerCase().slice(0, 3))
      if (i >= 0) out.add(i)
    }
  }
  return [...out].sort((a, b) => a - b)
}

export function localClock(now: number, tzOffsetMin: number) {
  const d = new Date(now - tzOffsetMin * 60_000)
  return {
    weekday: d.getUTCDay(),
    minute: d.getUTCHours() * 60 + d.getUTCMinutes(),
    day: d.toISOString().slice(0, 10),
  }
}

const minuteOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))

// Due when it's a scheduled day and within the window after the set time.
export function checkinDue(s: CheckinSchedule, now: number, tzOffsetMin: number) {
  const c = localClock(now, tzOffsetMin)
  const start = minuteOf(s.time)
  return s.days.includes(c.weekday) && c.minute >= start && c.minute < start + CHECKIN_WINDOW_MIN
}

export const checkinToday = (s: CheckinSchedule | undefined, now: number, tzOffsetMin: number) =>
  !!s?.days.includes(localClock(now, tzOffsetMin).weekday)

export const scheduleText = (s: CheckinSchedule) =>
  `${s.days.map((d) => NAMES[d][0].toUpperCase() + NAMES[d].slice(1)).join(', ')} at ${s.time}`

// Local instants of a schedule inside [fromMs, toMs). Weekday and clock follow
// the device, so a row lands on the same day the events agenda already shows.
export function checkinStarts(
  schedule: CheckinSchedule | null | undefined,
  fromMs: number,
  toMs: number,
): number[] {
  if (!schedule || toMs <= fromMs) return []
  const time = parseCheckinTime(schedule.time)
  if (!time || schedule.days.length === 0) return []
  const hh = Number(time.slice(0, 2))
  const mm = Number(time.slice(3, 5))
  const wanted = new Set(schedule.days)
  const out: number[] = []
  const origin = new Date(fromMs)
  const cursor = new Date(origin.getFullYear(), origin.getMonth(), origin.getDate())
  for (let i = 0; i < 400 && cursor.getTime() < toMs; i++) {
    if (wanted.has(cursor.getDay())) {
      const at = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        cursor.getDate(),
        hh,
        mm,
      ).getTime()
      if (at >= fromMs && at < toMs) out.push(at)
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}
